import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bookmark, BookmarkCheck, Repeat, MessageCircle, Share2 } from "lucide-react";
import { SunGlyph } from "@/components/GrowthGlyphs";
import { toast } from "sonner";

/**
 * SeedActionRow — IG-style action row inside an opened seed.
 *
 *   [ Sun ] [ Regrow ] [ Share ] [ Comment ]                     [ Save ]
 *   Given sun by holly_marie_designs and others
 *
 * Icons only (no labels), sized ~28px to match the reference IG layout.
 * The tapped state on Sun is unmistakable — filled glyph + honey tint on
 * the button + a persistent "Given sun by …" line below the row.
 *
 * Interaction rules:
 *   - Sun tap fires a light haptic on Android/Chrome (iOS no-ops silently)
 *     and calls parent's onSunTap so the seed hero can pulse a warm glow.
 *   - First-time viewers get a one-time tooltip anchored on the Sun icon.
 *   - Author of the seed can't Sun or Regrow their own (button hidden).
 */
const SUN_TOOLTIP_KEY = "willoah.sun_tooltip_seen.v1";
const ICON_SIZE = "w-7 h-7"; // ~28px for regrow/share/comment/save
// The Sun is the primary affordance — make it visibly larger than its
// neighbours so it reads as the star of the row (Kelly's Feb 22 revision).
const SUN_ICON_SIZE = "w-12 h-12"; // ~48px, ~1.7× the other icons
const ACTION_BUTTON =
  "inline-flex items-center justify-center rounded-full p-2 transition-colors";

export default function SeedActionRow({
  idea,
  saved,
  onToggleSave,
  onShare,
  onCommentJump,
  onSunTap, // parent hook for the hero-image warm glow
}) {
  const { user } = useAuth();
  const isAuthor = !!user && user.user_id === idea.author_id;
  const [sunByMe, setSunByMe] = useState(!!idea.sun_by_me);
  const [sunCount, setSunCount] = useState(idea.sun_count || 0);
  const [sunTappers, setSunTappers] = useState(idea.recent_sun_tappers || []);
  const [sunBusy, setSunBusy] = useState(false);
  const [regrown, setRegrown] = useState(false);
  const [regrowBusy, setRegrowBusy] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const tooltipTimerRef = useRef(null);
  // Bounce animation flag — when the viewer taps Sun the icon itself rises,
  // spins a whisker, and settles. Runs alongside the hero glow + haptic to
  // make the moment feel tactile (Kelly's Feb 22 revision).
  const [sunBounce, setSunBounce] = useState(false);
  const sunBounceTimerRef = useRef(null);
  useEffect(() => () => {
    if (sunBounceTimerRef.current) clearTimeout(sunBounceTimerRef.current);
  }, []);

  useEffect(() => {
    if (!user || isAuthor) { setRegrown(false); return; }
    let cancelled = false;
    api.get(`/ideas/${idea.id}/regrow`)
      .then(({ data }) => { if (!cancelled) setRegrown(!!data.regrown); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user, isAuthor, idea.id]);

  useEffect(() => {
    if (typeof idea.sun_by_me === "boolean") setSunByMe(idea.sun_by_me);
    if (typeof idea.sun_count === "number") setSunCount(idea.sun_count);
    if (Array.isArray(idea.recent_sun_tappers)) setSunTappers(idea.recent_sun_tappers);
  }, [idea.sun_by_me, idea.sun_count, idea.recent_sun_tappers]);

  useEffect(() => {
    if (!user || isAuthor) return;
    try { if (window.localStorage.getItem(SUN_TOOLTIP_KEY)) return; } catch { return; }
    const openTimer = setTimeout(() => setTooltipOpen(true), 500);
    tooltipTimerRef.current = setTimeout(() => {
      setTooltipOpen(false);
      try { window.localStorage.setItem(SUN_TOOLTIP_KEY, "1"); } catch {}
    }, 6500);
    return () => {
      clearTimeout(openTimer);
      if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
    };
  }, [user, isAuthor]);

  const dismissTooltip = () => {
    if (!tooltipOpen) return;
    setTooltipOpen(false);
    try { window.localStorage.setItem(SUN_TOOLTIP_KEY, "1"); } catch {}
  };

  const toggleSun = async () => {
    dismissTooltip();
    if (!user) return toast("Sign in to give a seed sun.");
    if (isAuthor) return toast("You planted this — no need to give yourself sun.");
    if (sunBusy) return;
    try { window.navigator?.vibrate?.(15); } catch {}
    // Tactile moment: bounce the button, glow the hero, haptic on mobile.
    // We always bounce (even on undo) so tapping feels consistent.
    setSunBounce(true);
    if (sunBounceTimerRef.current) clearTimeout(sunBounceTimerRef.current);
    sunBounceTimerRef.current = setTimeout(() => setSunBounce(false), 640);
    onSunTap?.();
    setSunBusy(true);
    const next = !sunByMe;
    setSunByMe(next);
    setSunCount((c) => Math.max(0, c + (next ? 1 : -1)));
    // Optimistic byline update — insert/remove viewer's tap at the head so
    // the "Given sun by …" line reflects the change without a refetch.
    setSunTappers((prev) => {
      const me = { user_id: user.user_id, name: user.name || "You" };
      const withoutMe = prev.filter((t) => t.user_id !== user.user_id);
      return next ? [me, ...withoutMe].slice(0, 5) : withoutMe;
    });
    try {
      const { data } = next
        ? await api.post(`/ideas/${idea.id}/sun`)
        : await api.delete(`/ideas/${idea.id}/sun`);
      if (typeof data?.sun_count === "number") setSunCount(data.sun_count);
      if (typeof data?.sun_by_me === "boolean") setSunByMe(data.sun_by_me);
    } catch (err) {
      setSunByMe(!next);
      setSunCount((c) => Math.max(0, c + (next ? -1 : 1)));
      setSunTappers(idea.recent_sun_tappers || []);
      toast(err?.response?.data?.detail || "Could not update.");
    } finally {
      setSunBusy(false);
    }
  };

  const toggleRegrow = async () => {
    if (!user) return toast("Sign in to regrow a seed.");
    if (isAuthor) return toast("You planted this seed — no need to regrow it.");
    if (regrowBusy) return;
    try { window.navigator?.vibrate?.(15); } catch {}
    setRegrowBusy(true);
    const next = !regrown;
    setRegrown(next);
    try {
      next
        ? await api.post(`/ideas/${idea.id}/regrow`)
        : await api.delete(`/ideas/${idea.id}/regrow`);
      toast(next ? "Regrown — thanks for spreading a good one." : "Undid regrow.");
    } catch (err) {
      setRegrown(!next);
      toast(err?.response?.data?.detail || "Could not update regrow.");
    } finally {
      setRegrowBusy(false);
    }
  };

  // "Given sun by X and others" line — IG copy pattern.
  const renderSunByline = () => {
    if (!sunCount) return null;
    // Prefer the viewer as the "primary" name when they've tapped Sun —
    // matches IG's "Liked by you and others" affordance.
    const orderedTappers = [
      ...(sunByMe ? [{ user_id: user?.user_id, name: "you" }] : []),
      ...sunTappers.filter((t) => !user || t.user_id !== user.user_id),
    ];
    const primary = orderedTappers[0];
    if (!primary) return null;
    const others = sunCount - 1;
    return (
      <div
        data-testid="sun-byline"
        className="mt-3 flex items-center gap-2 text-sm text-[#2A2823]/85"
      >
        {/* Small sun coin so the line reads as an endorsement, not a caption. */}
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#E4C078]/25 border border-[#B79256]/40 shrink-0">
          <SunGlyph filled className="w-3.5 h-3.5 text-[#8B6E3F]" />
        </span>
        <span className="leading-snug">
          Given sun by{" "}
          {primary.name === "you" || (user && primary.user_id === user.user_id) ? (
            <b className="font-medium">you</b>
          ) : (
            <Link
              to={`/roots/${primary.user_id}`}
              className="font-medium hover:underline decoration-[#B79256]/60"
            >
              {primary.name}
            </Link>
          )}
          {others > 0 && (
            <> and <span className="font-medium">others</span></>
          )}
        </span>
      </div>
    );
  };

  return (
    <div className="mt-6 no-print">
      <div
        data-testid="seed-action-row"
        className="flex items-center justify-between border-t border-b border-[#B79256]/25 py-2"
      >
        {/* LEFT — four primary actions, icons only (IG-style) */}
        <div className="flex items-center gap-1 sm:gap-2 relative">
          <div className="relative">
            <button
              type="button"
              data-testid="seed-action-sun"
              onClick={toggleSun}
              disabled={sunBusy}
              aria-label={sunByMe ? "Take back sun" : "Give this seed sun"}
              title={sunByMe ? "You gave this sun — tap to take it back" : "Give it Sun — Willoah's way of saying 'this is good.'"}
              className={`${ACTION_BUTTON} ${sunBounce ? "willoah-sun-bounce" : ""} ${
                sunByMe
                  ? "text-[#B79256] hover:bg-[#E4C078]/20"
                  : "text-[#2A2823] hover:bg-[#B79256]/10 hover:text-[#8B6E3F]"
              }`}
            >
              <SunGlyph filled={sunByMe} className={SUN_ICON_SIZE} />
            </button>
            {tooltipOpen && (
              <div
                data-testid="sun-tooltip"
                role="tooltip"
                onClick={dismissTooltip}
                className="absolute z-30 top-full mt-2 left-0 w-64 sm:w-72 rounded-2xl border border-[#B79256]/50 bg-[#FBF8F1] px-4 py-3 shadow-lg text-left cursor-pointer"
              >
                <div className="flex items-start gap-2">
                  <SunGlyph className="w-4 h-4 text-[#B79256] mt-0.5 shrink-0" />
                  <div>
                    <p className="font-serif-display text-sm text-[#2A2823] leading-snug">
                      <b className="font-medium">Give it Sun</b> — Willoah's way of saying "this is good."
                    </p>
                    <p className="text-[11px] text-[#2A2823]/55 mt-1">Tap anywhere to dismiss.</p>
                  </div>
                </div>
                <div className="absolute -top-1.5 left-6 w-3 h-3 rotate-45 bg-[#FBF8F1] border-t border-l border-[#B79256]/50" />
              </div>
            )}
          </div>

          {!isAuthor && (
            <button
              type="button"
              data-testid="seed-action-regrow"
              onClick={toggleRegrow}
              disabled={regrowBusy}
              aria-label={regrown ? "Undo regrow" : "Regrow this seed"}
              title={regrown ? "You regrew this — tap to undo" : "Regrow — reshare this seed to your followers"}
              className={`${ACTION_BUTTON} ${
                regrown
                  ? "text-[#4E5A3D] hover:bg-[#4E5A3D]/10"
                  : "text-[#2A2823] hover:bg-[#B79256]/10"
              }`}
            >
              <Repeat className={ICON_SIZE} strokeWidth={1.75} />
            </button>
          )}

          <button
            type="button"
            data-testid="seed-action-share"
            onClick={onShare}
            aria-label="Share this seed"
            title="Share this seed"
            className={`${ACTION_BUTTON} text-[#2A2823] hover:bg-[#B79256]/10`}
          >
            <Share2 className={ICON_SIZE} strokeWidth={1.75} />
          </button>

          <button
            type="button"
            data-testid="seed-action-comment"
            onClick={onCommentJump}
            aria-label="Jump to Field Notes"
            title="Jump to Field Notes (comments)"
            className={`${ACTION_BUTTON} text-[#2A2823] hover:bg-[#B79256]/10`}
          >
            <MessageCircle className={ICON_SIZE} strokeWidth={1.75} />
          </button>
        </div>

        {/* RIGHT — Save (IG bookmark on the right) */}
        <button
          type="button"
          data-testid="seed-action-save"
          onClick={onToggleSave}
          aria-label={saved ? "Remove from Your Grove" : "Save to Your Grove"}
          title={saved ? "Saved to Your Grove" : "Save to Your Grove"}
          className={`${ACTION_BUTTON} ${
            saved
              ? "text-[#4E5A3D] hover:bg-[#4E5A3D]/10"
              : "text-[#2A2823] hover:bg-[#B79256]/10"
          }`}
        >
          {saved
            ? <BookmarkCheck className={ICON_SIZE} strokeWidth={1.75} />
            : <Bookmark className={ICON_SIZE} strokeWidth={1.75} />}
        </button>
      </div>
      {renderSunByline()}
    </div>
  );
}
